import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Spinner,
  Table,
} from "react-bootstrap";
import { useStaffPermissions } from "../../hooks";
import { staffApiError } from "../apiErrors";
import { AUDIT_PAGE_SIZE, useAuditLog, useStaffList } from "../hooks";
import {
  ACTION_LABELS,
  AuditFilters,
  RESOURCE_LABELS,
} from "../components/AuditFilters";

const EMPTY_FILTERS = { actor: "", action: "", resource: "", from: "", to: "" };

function formatDateTime(v) {
  return v ? new Date(v).toLocaleString("es-CO") : "—";
}

/**
 * Celda "Actor". Las filas de sistema (tareas Celery) llegan como
 * `{ id: null, name: "sistema" }` y **sin la clave `email`**, así que nunca se
 * lee `actor.email` sin comprobar el id primero.
 */
function ActorCell({ actor, onFilterByActor }) {
  if (!actor || actor.id == null) {
    return <Badge bg="secondary">{actor?.name || "sistema"}</Badge>;
  }
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0 text-start"
      onClick={() => onFilterByActor(actor.id)}
      aria-label={`Filtrar la bitácora por ${actor.email ?? actor.name}`}
    >
      <span className="d-block">{actor.email ?? actor.name}</span>
      {actor.email && <span className="text-muted small">{actor.name}</span>}
    </Button>
  );
}

function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { data, isLoading, isError, error, isFetching } = useAuditLog({
    page,
    ...filters,
  });

  // El desplegable de actores necesita `superadmin/staff/`, que solo puede leer
  // un PLATFORM_ADMIN; un READ_ONLY ve la bitácora pero recibiría 403 aquí.
  const { can } = useStaffPermissions();
  const { data: staff } = useStaffList({ enabled: can("staff", "read") });

  // Cambiar un filtro vuelve siempre a la página 1: la paginación es
  // server-side y la página N del filtro anterior no existe en el nuevo.
  const patchFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const filterByActor = useCallback(
    (actorId) => patchFilter("actor", String(actorId)),
    [patchFilter]
  );

  const columns = useMemo(
    () => [
      {
        id: "created_at",
        header: "Fecha",
        accessorFn: (row) => row.created_at,
        cell: ({ getValue }) => (
          <span className="small">{formatDateTime(getValue())}</span>
        ),
      },
      {
        id: "actor",
        header: "Actor",
        cell: ({ row }) => (
          <ActorCell actor={row.original.actor} onFilterByActor={filterByActor} />
        ),
      },
      {
        id: "action",
        header: "Acción",
        accessorFn: (row) => row.action,
        cell: ({ getValue }) => ACTION_LABELS[getValue()] ?? getValue(),
      },
      {
        id: "resource",
        header: "Recurso",
        accessorFn: (row) => row.resource,
        cell: ({ getValue }) => RESOURCE_LABELS[getValue()] ?? getValue(),
      },
      {
        id: "target",
        header: "Objetivo",
        cell: ({ row }) => (
          <span>
            {row.original.target_repr || "—"}
            {row.original.target_id != null && (
              <span className="text-muted small"> #{row.original.target_id}</span>
            )}
          </span>
        ),
      },
      {
        id: "ip",
        header: "IP",
        accessorFn: (row) => row.ip ?? "—",
        cell: ({ getValue }) => <span className="text-muted small">{getValue()}</span>,
      },
      {
        id: "metadata",
        header: "Detalle",
        cell: ({ row }) => {
          const meta = row.original.metadata;
          if (!meta || Object.keys(meta).length === 0) return "—";
          return (
            <details>
              <summary className="small">ver</summary>
              <pre className="small mb-0">{JSON.stringify(meta, null, 2)}</pre>
            </details>
          );
        },
      },
    ],
    [filterByActor]
  );

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / AUDIT_PAGE_SIZE));

  // Tabla SIN modelo de paginación: `rows` ya es una página del servidor. No se
  // usa manualPagination porque lo único que aportaría es getPageCount() —una
  // división— a cambio de duplicar el estado de paginación y sincronizarlo con
  // la clave de la query.
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container fluid className="py-4">
      <h4 className="mb-3">Bitácora de auditoría</h4>

      <AuditFilters
        filters={filters}
        onChange={patchFilter}
        onReset={resetFilters}
        staffOptions={staff}
      />

      {isError && (
        <Alert variant="danger">
          {staffApiError(error, "No se pudo cargar la bitácora.")}
        </Alert>
      )}

      {isLoading && (
        <div className="py-5 text-center">
          <Spinner aria-label="Cargando bitácora" />
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Alert variant="light" className="border">
          No hay registros que coincidan con el filtro.
        </Alert>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          {/* ── Escritorio: tabla ── */}
          <div className="d-none d-sm-block">
            <div className="table-responsive">
              <Table hover bordered size="sm" className="align-middle">
                <thead className="table-light">
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((h) => (
                        <th key={h.id}>
                          {flexRender(h.column.columnDef.header, h.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {/* ── Móvil: tarjetas ── */}
          <div className="d-sm-none">
            {rows.map((row) => (
              <Card key={row.id} className="mb-2">
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <strong className="small">
                      {ACTION_LABELS[row.action] ?? row.action}
                    </strong>
                    <Badge bg="light" text="dark">
                      {RESOURCE_LABELS[row.resource] ?? row.resource}
                    </Badge>
                  </div>
                  <div className="small mt-1">
                    {row.target_repr || "—"}
                    {row.target_id != null && (
                      <span className="text-muted"> #{row.target_id}</span>
                    )}
                  </div>
                  <div className="mt-1">
                    <ActorCell actor={row.actor} onFilterByActor={filterByActor} />
                  </div>
                  <div className="text-muted small mt-1">
                    {formatDateTime(row.created_at)}
                    {row.ip && ` · ${row.ip}`}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Paginación server-side: `next`/`previous` del envelope DRF gobiernan
          los botones (más fiable que aritmética sobre `count`). */}
      <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={!data?.previous || isFetching}
          aria-label="Página anterior"
        >
          ‹ Anterior
        </Button>
        <span className="small text-muted">
          Página {page} de {totalPages} · {count} registro(s)
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setPage((p) => p + 1)}
          disabled={!data?.next || isFetching}
          aria-label="Página siguiente"
        >
          Siguiente ›
        </Button>
      </div>
    </Container>
  );
}

export { AuditLogPage };
